import {
  FULL_SCORE_AVERAGE_STARS,
  ZERO_SCORE_AVERAGE_STARS,
  STARS_DECIMALS
} from "@/utils/averageStarsThresholds";
import {
  CURRENT_FORMULA_WEIGHTS,
  PREVIOUS_FORMULA_WEIGHTS,
  PREVIOUS_FORMULA_FULL_SCORE_STARS,
  PREVIOUS_FORMULA_ZERO_SCORE_STARS
} from "@/utils/levelFormulaWeights";
import {
  WAIT_TOLERANCE_MINUTES,
  FULL_SCORE_WAIT_EXCESS_MINUTES,
  ZERO_SCORE_WAIT_EXCESS_MINUTES,
  LEVEL_EXCESS_CAP_MINUTES,
  MAX_PLAUSIBLE_WAIT_HOURS
} from "@/utils/dealerWaitThresholds";

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_PART_LENGTH = 2;
const DECIMAL_SEPARATOR = ",";
const MONEY_DECIMALS = 2;
const UNKNOWN_CHANGE_DAY_LABEL = "cambio de fórmula";

const formatDayMonth = (isoDate) => {
  const [, , month, day] = isoDate.match(ISO_DATE);
  return `${day}/${month}`;
};

const dayBefore = (isoDate) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return new Date(date.getTime() - MILLISECONDS_PER_DAY).toISOString().slice(0, 10);
};

export const formatFormulaChangeDay = (changeDate) => (changeDate ? formatDayMonth(changeDate) : UNKNOWN_CHANGE_DAY_LABEL);

export const formatLastPreviousFormulaDay = (changeDate) =>
  changeDate ? formatDayMonth(dayBefore(changeDate)) : UNKNOWN_CHANGE_DAY_LABEL;

export const sortChargeTiersByMinutes = (chargeTiers) =>
  [...chargeTiers].sort((first, second) => first.minMinutes - second.minMinutes);

const formatStars = (stars) => stars.toFixed(STARS_DECIMALS).replace(".", DECIMAL_SEPARATOR);

export const formatAmount = (amount) => {
  const hasCents = !Number.isInteger(amount);
  return amount.toFixed(hasCents ? DATE_PART_LENGTH : 0).replace(".", DECIMAL_SEPARATOR);
};

export const formatMoney = (currency, amount) =>
  `${currency || ""} ${Number(amount).toFixed(MONEY_DECIMALS).replace(".", DECIMAL_SEPARATOR)}`.trim();

export const buildLevelHelpText = (changeDate) => `Tu nivel sale de sumar los tres puntajes con su peso: más de 2 es A, más de 1 y hasta 2 es B, 1 o menos es C. El nivel define el precio que se te paga por plato. Desde el ${formatFormulaChangeDay(changeDate)} los pesos son reclamos ${CURRENT_FORMULA_WEIGHTS.claims} %, estrellas ${CURRENT_FORMULA_WEIGHTS.stars} % y espera del repartidor ${CURRENT_FORMULA_WEIGHTS.dealerWait} %; antes eran reclamos ${PREVIOUS_FORMULA_WEIGHTS.claims} %, tardanza ${PREVIOUS_FORMULA_WEIGHTS.lateness} % y estrellas ${PREVIOUS_FORMULA_WEIGHTS.stars} %.`;

export const REFERENTIAL_SCORE_HELP_TEXT =
  "Puntaje calculado con la fórmula que regía en esa quincena. La letra es la que se usó para tu pago.";

export const INCIDENTS_HELP_TEXT =
  "Reclamos de tus clientes, pesados por gravedad, por cada pedido entregado. Sumas 3 puntos con 0,5 o menos por cada 100 pedidos y 0 puntos desde 1,5.";

export const DEALER_WAIT_HELP_TEXT = `Minutos que el repartidor espera en tu cocina después de la hora de salida de la ruta, descontando ${WAIT_TOLERANCE_MINUTES} minutos de margen. Se mide con lo que el repartidor marca en su app: llegada e inicio de ruta. Sumas 3 puntos con ${FULL_SCORE_WAIT_EXCESS_MINUTES} minutos o menos de exceso promedio y 0 puntos desde ${ZERO_SCORE_WAIT_EXCESS_MINUTES}. Si no hay marcas en el periodo, suma 0. En el promedio, cada ruta cuenta como máximo ${LEVEL_EXCESS_CAP_MINUTES} minutos. Las esperas de más de ${MAX_PLAUSIBLE_WAIT_HOURS} horas no se cuentan, porque son un error de marca.`;

export const buildLatenessHelpText = (changeDate) =>
  `Cálculo anterior, usado hasta el ${formatLastPreviousFormulaDay(changeDate)}: porcentaje de rutas que salieron tarde según el registro del supervisor.`;

export const RATINGS_HELP_TEXT = `Promedio de estrellas que te dan tus clientes. Sumas 3 puntos con ${formatStars(
  FULL_SCORE_AVERAGE_STARS
)} o más y 0 puntos con ${formatStars(ZERO_SCORE_AVERAGE_STARS)} o menos. En las quincenas calculadas con la fórmula de antes eran ${formatStars(
  PREVIOUS_FORMULA_FULL_SCORE_STARS
)} y ${formatStars(PREVIOUS_FORMULA_ZERO_SCORE_STARS)}.`;


export const buildWaitChargeHelpText = (amountTiers, changeDate) => {
  const chargedTiers = sortChargeTiersByMinutes(amountTiers).filter((tier) => tier.amount > 0);
  const lastTier = chargedTiers[chargedTiers.length - 1];
  const tiersText = chargedTiers
    .map((tier) => {
      const minutes = tier === lastTier && chargedTiers.length > 1 ? `${tier.minMinutes} min o más` : `${tier.minMinutes} min`;
      return `${minutes} ${tier.currency} ${formatAmount(tier.amount)}`;
    })
    .join(" · ");

  return `Desde el ${formatFormulaChangeDay(changeDate)}, si el repartidor espera más de ${WAIT_TOLERANCE_MINUTES} minutos, se descuenta de tu pago según el exceso: ${tiersText}. Una ruta sin marcas no se cobra.`;
};
